import { ChevronDown, LogOut } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover } from '@/components/ui/popover'
import { useAuthSession, useMe } from '@/lib/auth'

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? '?'
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return `${first}${last}`.toUpperCase()
}

/** Perfis em português no menu (o valor técnico fica no `title`). */
const PROFILE_LABELS: Record<string, string> = {
  viewer: 'Leitura',
  operator: 'Operador',
  admin: 'Administrador',
  service: 'Serviço',
}

export function UserMenu() {
  const session = useAuthSession()
  const me = useMe()
  const name = me.data?.name ?? session.profile?.name ?? me.data?.username ?? 'Usuário'
  const email = me.data?.email ?? session.profile?.email ?? null
  const roles = me.data?.profiles ?? []
  // Como o legado: claim `picture` do token (via /me; id token como reserva).
  const picture = me.data?.picture ?? session.profile?.picture ?? null

  return (
    <Popover
      align="end"
      className="w-72"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-haspopup="true"
          aria-label={`Menu do usuário ${name}`}
          className="flex items-center gap-2 rounded-lg px-1.5 py-1 text-sm transition-colors hover:bg-muted"
        >
          <Avatar
            src={picture}
            fallback={initials(name)}
            imgTestId="user-menu-avatar"
            className="h-8 w-8 border-2 border-brand-600 bg-brand-100 text-brand-700 dark:bg-brand-800 dark:text-brand-200"
          />
          <span className="hidden max-w-[10rem] truncate font-medium md:inline">{name}</span>
          <ChevronDown className="hidden h-3.5 w-3.5 text-muted-foreground md:inline" aria-hidden />
        </button>
      )}
    >
      {() => (
        <div className="space-y-2">
          <div className="px-2 pb-1 pt-1">
            <p className="truncate text-sm font-semibold" data-testid="user-menu-name">
              {name}
            </p>
            {email ? (
              <p className="truncate text-xs text-muted-foreground" data-testid="user-menu-email">
                {email}
              </p>
            ) : null}
            {roles.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-1">
                {roles.map((role) => (
                  <Badge key={role} tone="neutral" title={role}>
                    {PROFILE_LABELS[role] ?? role}
                  </Badge>
                ))}
              </div>
            ) : null}
            {session.mode === 'mock' ? (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                Sessão simulada (modo mock). Troque a persona com <code>?mock_role=</code>.
              </p>
            ) : null}
          </div>
          <div className="border-t border-border pt-2">
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={() => void session.signOut()}
            >
              <LogOut className="h-4 w-4" aria-hidden />
              Sair
            </Button>
          </div>
        </div>
      )}
    </Popover>
  )
}
